import React, { useCallback, useEffect, useState } from 'react';
import { api, openAuthedFile } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { StatusBadge } from '@/components/StatusBadge';
import { Search, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '@/context/AppContext';

export default function Archive() {
  const { role } = useApp();
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const navigate = useNavigate();

  const load = useCallback(async () => {
    const r = await api.get('/records/search', { params: { q } });
    setItems(r.data || []);
  }, [q]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-4 lg:p-6 max-w-7xl mx-auto">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <h1 className="text-base lg:text-lg font-semibold tracking-tight">Records Archive</h1>
        <div className="flex-1" />
        <div className="relative flex-1 min-w-[200px] sm:flex-none">
          <Search className="h-4 w-4 absolute left-2 top-2.5 text-muted-foreground" />
          <Input className="pl-7 w-full sm:w-72" placeholder="Search by patient, employer, ICD-10, procedure" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && load()} data-testid="archive-search" />
        </div>
        <Button onClick={load} data-testid="archive-search-button">Search</Button>
      </div>
      {/* Phone: card list */}
      <div className="md:hidden space-y-3" data-testid="archive-cards">
        {items.length === 0 && <Card className="p-8 text-center text-sm text-muted-foreground">No records.</Card>}
        {items.map((it) => (
          <Card key={it.id} className="p-4 space-y-2" data-testid={`archive-card-${it.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-medium truncate">{it.patient_form?.full_name || '—'}</p>
                <p className="text-xs text-muted-foreground truncate">{it.patient_form?.employer_name || '—'}</p>
              </div>
              <StatusBadge status={it.status} />
            </div>
            <p className="text-xs text-muted-foreground truncate">
              {it.nurse_clinical?.icd10} {it.nurse_clinical?.procedure_name ? `· ${it.nurse_clinical.procedure_name}` : ''}
              {it.updated_at ? ` · ${it.updated_at.slice(0, 10)}` : ''}
            </p>
            <div className="flex gap-2 pt-1">
              <Button className="flex-1" variant="outline" onClick={() => navigate(role === 'doctor' ? `/doctor/review/${it.id}` : `/nurse/submission/${it.id}`)} data-testid={`archive-open-card-${it.id}`}>Open</Button>
              {it.status === 'complete' && <Button className="flex-1" variant="outline" onClick={() => openAuthedFile(`/submissions/${it.id}/pdf`, `${(it.patient_form?.full_name || 'packet').replace(/\s+/g, '_')}_signed_packet.pdf`)} data-testid={`archive-pdf-card-${it.id}`}><Download className="h-3.5 w-3.5 mr-1" /> PDF</Button>}
            </div>
          </Card>
        ))}
      </div>

      {/* Tablet and up: table */}
      <Card className="hidden md:block">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="archive-table">
            <thead className="bg-card border-b">
              <tr className="text-left text-xs text-muted-foreground">
                <th className="px-3 py-2">Patient</th>
                <th className="px-3 py-2">Employer</th>
                <th className="px-3 py-2">Diagnosis / Procedure</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Updated</th>
                <th className="px-3 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={6} className="px-6 py-12 text-center text-muted-foreground">No records.</td></tr>}
              {items.map((it) => (
                <tr key={it.id} className="border-b hover:bg-muted/60" data-testid={`archive-row-${it.id}`}>
                  <td className="px-3 py-2 font-medium">{it.patient_form?.full_name || '—'}</td>
                  <td className="px-3 py-2">{it.patient_form?.employer_name || '—'}</td>
                  <td className="px-3 py-2">{it.nurse_clinical?.icd10} {it.nurse_clinical?.procedure_name ? `· ${it.nurse_clinical.procedure_name}` : ''}</td>
                  <td className="px-3 py-2"><StatusBadge status={it.status} /></td>
                  <td className="px-3 py-2 text-muted-foreground">{(it.updated_at || '').slice(0, 16).replace('T', ' ')}</td>
                  <td className="px-3 py-2">
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" onClick={() => navigate(role === 'doctor' ? `/doctor/review/${it.id}` : `/nurse/submission/${it.id}`)}>Open</Button>
                      {it.status === 'complete' && <Button size="sm" variant="outline" onClick={() => openAuthedFile(`/submissions/${it.id}/pdf`, `${(it.patient_form?.full_name || 'packet').replace(/\s+/g, '_')}_signed_packet.pdf`)} data-testid={`archive-pdf-${it.id}`}><Download className="h-3.5 w-3.5 mr-1" /> PDF</Button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
